import React from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import BasicButton from "../../components/basics/BasicButton";
import { useGetStoreSwitchQuery, useSetStoreSwitchMutation } from "./storeSwitchApi";
import { setStoreOpen } from "./storeOpen";
import "./storeSwitch.css";

// /admin/marketplace: open or close the n450s.com store.

function errMsg(e: unknown, fallback: string): string {
  const data = (e as { data?: { message?: string } })?.data;
  return (data && typeof data.message === "string" && data.message) || fallback;
}

function when(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const StoreSwitchPage: React.FC = () => {
  const { data, isLoading, isError, refetch } = useGetStoreSwitchQuery();
  const [save, { isLoading: saving }] = useSetStoreSwitchMutation();
  const open = data?.enabled;

  const flip = async () => {
    if (open === undefined || saving) return;
    try {
      const saved = await save({ enabled: !open }).unwrap();
      // so the header in this browser follows at once
      setStoreOpen(saved.enabled);
      toast.success(saved.enabled ? "the store is open" : "the store is closed");
    } catch (e) {
      toast.error(errMsg(e, "could not save the store switch"));
    }
  };

  return (
    <div className="ss-page">
      <div className="ss-content">
        <Link to="/admin" className="ss-back">
          ← admin
        </Link>
        <h1 className="ss-title">store switch</h1>

        {isLoading ? (
          <p className="ss-hint">loading...</p>
        ) : isError || open === undefined ? (
          <div className="ss-hint">
            <p>could not load the store switch.</p>
            <BasicButton text="try again" onClick={() => refetch()} />
          </div>
        ) : (
          <section className={`ss-state ${open ? "is-open" : "is-closed"}`} aria-live="polite">
            <p className="ss-state-label">the store is</p>
            <p className="ss-state-value">{open ? "open" : "closed"}</p>
            {data?.updatedAt && <p className="ss-state-when">since {when(data.updatedAt)}</p>}
            <BasicButton
              text={saving ? "saving..." : open ? "close the store" : "open the store"}
              onClick={flip}
              disabled={saving}
              fontWeight="700"
              bgColor={open ? "#ff9090" : "#60fac1e1"}
              hoverBgColor={open ? "#ff7070" : "#1b8e64e1"}
            />
          </section>
        )}

        <section className="ss-explain">
          <h2>while it is closed</h2>
          <ul>
            <li>The header has no store item and no cart count. Collectibles stays, as its own tab.</li>
            <li>/marketplace and the product pages say the store is closed.</li>
            <li>A card payment that comes back still lands on its order page.</li>
            <li>You still see the store, under a note, so you can check it before opening it.</li>
          </ul>
          <p>
            It only hides the store on n450s.com. 450labs still takes orders from anyone who has the
            shop's key until you pause the shop in the 450labs panel.
          </p>
          <p>
            <Link to="/marketplace">see the store page</Link>
          </p>
        </section>
      </div>
    </div>
  );
};

export default StoreSwitchPage;
